import { Accordion } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Skeleton, Tabs } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar.tsx';
import SearchBar from '../../components/Search/SearchBar';
import type { HealthMetricType } from '../../fetchHooks/useHealthmetrictypesWithMeasurements';
import type { MydayType } from '../../fetchHooks/useMyday.ts';
import { useUserProfile } from '../../fetchHooks/useUserProfile.ts';
import { CreateMyday } from './CreateMyday.jsx';
import { DiaryTable } from './DiaryTable.jsx';
import { EditMyday } from './EditMyday.jsx';
import { ExperiencesTable } from './ExperiencesTable.jsx';
import HealthMetricGraphs from './HealthMetricGraphs.tsx';
import { HealthRelatedTable } from './HealthRelatedTable.jsx';
import { MeasurementsTable } from './MeasurementsTable.jsx';
import { NotesTable } from './NotesTable.jsx';
import { SelectHealthMetricsChecklist } from './SelectHealthMetricsChecklist.tsx';
import { SelectHealthMetricsDropdown } from './SelectHealthMetricsDropdown.tsx';
import './Myday.scss';
import { FurutoppenPage } from './FurutoppenPage.tsx';

export enum MyDayVariant {
  DIARY = 'diary',
  MEASUREMENT = 'measurement',
  HEALTH_RELATED = 'health-related',
  NOTE = 'note',
  EXPERIENCE = 'experience',
  ALL = 'all',
}

export type MyDayVariantType =
  | MyDayVariant.DIARY
  | MyDayVariant.MEASUREMENT
  | MyDayVariant.HEALTH_RELATED
  | MyDayVariant.NOTE
  | MyDayVariant.EXPERIENCE
  | MyDayVariant.ALL;

const MyDayPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const handleShowMydayDetails = ({ id }: { id: number }) => navigate(`/mydaydetails/${id}`);
  const {
    data: userProfile,
    isLoading: isLoadingUserProfile,
    userHasServiceHealth,
    userHasServiceSimplified,
  } = useUserProfile();
  const [showBloodworkDescription, setShowBloodworkDescription] = useState(false);
  const [selectedGraphs, setSelectedGraphs] = useState<HealthMetricType[]>([]);
  const [showCreateMydayModal, setShowCreateMydayModal] = useState(false);
  const [showEditMydayModal, setShowEditMydayModal] = useState<MydayType | false>(false);
  const queryClient = useQueryClient();
  const isLoading = isLoadingUserProfile;

  const [activeKey, setActiveKey] = useState<string>(() => {
    return sessionStorage.getItem('mydayActiveTab') || MyDayVariant.EXPERIENCE;
  });
  if (userProfile?.username === 'furutoppen') {
    return <FurutoppenPage userProfile={userProfile} />;
  }

  const handleCreateMyday = () => {
    setShowCreateMydayModal(false);
    queryClient.invalidateQueries({ queryKey: ['useMydays'] });
    queryClient.invalidateQueries({ queryKey: ['useHealthmetrictypesWithMeasurements'] });
  };

  const onOk = () => {
    setShowEditMydayModal(false);
    queryClient.invalidateQueries({ queryKey: ['useMydays'] });
    queryClient.invalidateQueries({ queryKey: ['useHealthmetrictypesWithMeasurements'] });
  };

  type TabKey = string;

  const handleTabChange = (key: TabKey): void => {
    setActiveKey(key);
    sessionStorage.setItem('mydayActiveTab', key);
  };

  return (
    <div>
      <Navbar />
      <div className="app-container">
        <h2>
          {isLoading ? (
            <Skeleton.Input active size="default" style={{ width: 300 }} />
          ) : (
            userProfile?.firstname + "'s " + t('myday.title')
          )}
        </h2>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', marginBottom: '1rem' }}>
            <Button type="primary" onClick={() => setShowCreateMydayModal(true)}>
              {t('myday.createNew')}
            </Button>
            <Link className="button-link" to={`/export?tab=${activeKey}`}>
              {t('myday.exportToPDF')}
            </Link>
          </div>

          {/* Search Bar */}
          <div style={{ marginBottom: '1rem' }}>
            <SearchBar
              placeholder={t('myday.searchPlaceholder', 'Search across all your entries...')}
              onResultSelect={(result, type) => {
                console.log('Search result selected:', result, type);
                if (type === 'mydays' && result.id) {
                  handleShowMydayDetails(result);
                }
              }}
            />
          </div>
          <Tabs
            activeKey={activeKey}
            onChange={handleTabChange}
            items={[
              ...(!userHasServiceSimplified
                ? [
                    {
                      label: t('myday.experience'),
                      key: MyDayVariant.EXPERIENCE,
                      children: <ExperiencesTable setShowEditMydayModal={setShowEditMydayModal} />,
                    },
                  ]
                : []),
              {
                label: t('myday.diary'),
                key: MyDayVariant.DIARY,
                children: <DiaryTable setShowEditMydayModal={setShowEditMydayModal} />,
              },
              ...(userHasServiceHealth
                ? [
                    {
                      label: t('myday.measurement'),
                      key: MyDayVariant.MEASUREMENT,
                      children: <MeasurementsTable setShowEditMydayModal={setShowEditMydayModal} />,
                    },
                  ]
                : []),
              ...(userHasServiceHealth
                ? [
                    {
                      label: t('myday.healthRelated'),
                      key: MyDayVariant.HEALTH_RELATED,
                      children: <HealthRelatedTable setShowEditMydayModal={setShowEditMydayModal} />,
                    },
                  ]
                : []),
              {
                label: t('myday.note'),
                key: MyDayVariant.NOTE,
                children: <NotesTable setShowEditMydayModal={setShowEditMydayModal} />,
              },
            ]}
          />
          {showCreateMydayModal && (
            <CreateMyday
              showModal={showCreateMydayModal}
              onClose={() => setShowCreateMydayModal(false)}
              onOk={handleCreateMyday}
              userProfile={userProfile}
              type={activeKey as MyDayVariantType}
            />
          )}
          {showEditMydayModal && (
            <EditMyday
              showModal={!!showEditMydayModal}
              onClose={() => setShowEditMydayModal(false)}
              onOk={onOk}
              userProfile={userProfile}
              myday={showEditMydayModal as MydayType}
            />
          )}
        </div>
      </div>
      {userHasServiceHealth && (
        <div className="app-container">
          <div>
            <h2>{t('myday.measurementGraph')}</h2>
            <Accordion>
              <Accordion.Item value="measurement-graph">
                <Accordion.Control>
                  <SelectHealthMetricsDropdown
                    selectedGraphs={selectedGraphs}
                    setSelectedGraphs={setSelectedGraphs}
                    showBloodworkDescription={showBloodworkDescription}
                    setShowBloodworkDescription={setShowBloodworkDescription}
                  />
                </Accordion.Control>
                <Accordion.Panel>
                  <SelectHealthMetricsChecklist selectedGraphs={selectedGraphs} setSelectedGraphs={setSelectedGraphs} />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
            <HealthMetricGraphs showBloodworkDescription={showBloodworkDescription} selectedGraphs={selectedGraphs} />
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDayPage;
